import * as Sentry from '@sentry/browser';
import { QueuedIntakeFileInfo, UploadData } from '../../../store/uploadStore';

type UploadCallback = (span: Sentry.Span) => Promise<void>;

const batchSpans = new Map<
  string,
  {
    span: Sentry.Span;
    timeoutId: NodeJS.Timeout;
  }
>();

const BATCH_SPAN_TIMEOUT = 5 * 60 * 1000;

const getOrCreateBatchSpan = (upload: UploadData) => {
  const key = `${upload.upload!.customer}-${upload.upload!.site}-${upload.meta.type}`;

  if (batchSpans.has(key)) {
    clearTimeout(batchSpans.get(key)!.timeoutId);
  } else {
    const span = Sentry.startInactiveSpan({
      name: `upload-${key}`,
    });

    batchSpans.set(key, { span, timeoutId: setTimeout(() => {}, 0) });
  }

  const timeoutId = setTimeout(() => {
    if (batchSpans.has(key)) {
      batchSpans.get(key)!.span.end();
      batchSpans.delete(key);
    }
  }, BATCH_SPAN_TIMEOUT);

  const entry = batchSpans.get(key)!;
  entry.timeoutId = timeoutId;

  return entry.span;
};

/**
 * Creates a batch span if it doesn't exist.
 * Closes it after 5 minutes of inactivity. (Will open a new one if the upload queue gets updated).
 * There is no need to close the batch span when user tries to leave the page - Sentry takes care of it.
 */
export const executeUploadWithSentrySpan = async (
  upload: UploadData,
  file: QueuedIntakeFileInfo,
  uploadCallback: UploadCallback,
) => {
  const batchSpan = getOrCreateBatchSpan(upload);

  return Sentry.startSpan(
    {
      name: `PUT S3 ${upload.upload!.uri}/${file.name}`,
      attributes: {
        customerId: upload.upload!.customer,
        siteId: upload.upload!.site,
        uploadPath: upload.upload!.uri,
        fileUploadStartTime: Date.now(),
      },
      parentSpan: batchSpan,
      op: 'http.client',
    },
    async (span) => {
      await uploadCallback(span);
    },
  );
};

export const setUploadAttributesOnSentrySpan = (span: Sentry.Span, sizeBytes: number, startTimeMs: number) => {
  const endTimeMs = Date.now();

  const fileUploadTimeSeconds = Math.round((endTimeMs - startTimeMs) / 1000);
  const speedBytesPerSeconds = sizeBytes / ((endTimeMs - startTimeMs) * 1000);

  // @TODO Why? Registering times is the point of spans
  span.setAttributes({
    fileUploadEndTime: endTimeMs,
    fileUploadTimeSeconds,
    speedBytesPerSeconds,
  });
};
